import BigNumber from 'bignumber.js'

/**
 * Dollars, for a mode that only has dollars.
 *
 * Basic holds one balance in USDC and buys on any chain out of it. Nothing in
 * it is denominated in SOL or ETH, so there is no token to pick and no gas to
 * keep back — a screen shows a number with two decimal places and that is the
 * whole of the model.
 *
 * Everything crosses the wire in the smallest unit, as a string. That is the
 * only representation that survives a balance of eight dollars and a token
 * priced at a ten-thousandth of a cent in the same request, and the reason
 * none of this uses `Number`.
 */

/** USDC has six places on every chain we settle on. */
export const USDC_DECIMALS = 6

/** Smallest units as dollars. `"8000000"` is 8. */
export const toDollars = (
  raw: string,
  decimals: number = USDC_DECIMALS,
): BigNumber => {
  const value = BigNumber(raw)

  return value.isFinite() ? value.shiftedBy(-decimals) : BigNumber(0)
}

/**
 * Dollars somebody typed, as the smallest unit the API takes.
 *
 * Rounded down, always. A third of a cent rounded up is a third of a cent the
 * balance does not have, and the buy comes back refused for an amount the
 * screen itself put in the field.
 */
export const toRaw = (
  dollars: BigNumber.Value,
  decimals: number = USDC_DECIMALS,
): string => {
  const value = BigNumber(dollars)

  if (!value.isFinite() || value.isNegative()) {
    return '0'
  }

  return value.shiftedBy(decimals).integerValue(BigNumber.ROUND_DOWN).toFixed(0)
}

/** Two places with separators: what a person reads. */
export const formatUsd = (value: BigNumber.Value): string => {
  const amount = BigNumber(value)

  if (!amount.isFinite()) {
    return '$0.00'
  }

  // Anything under half a cent would print as $0.00 and read as nothing at
  // all, which is wrong for a holding somebody paid for.
  if (amount.isGreaterThan(0) && amount.isLessThan(0.01)) {
    return '<$0.01'
  }

  const sign = amount.isNegative() ? '-' : ''

  return `${sign}$${amount.absoluteValue().toFormat(2)}`
}

/**
 * Dollars for a button: "$10", not "$10.00". Cents stay when there are any,
 * so a button never shows less than it spends.
 */
export const formatUsdCompact = (value: BigNumber.Value): string =>
  formatUsd(value).replace(/\.00$/, '')

/** A change, always signed, so up and down read without comparing. */
export const formatSignedUsd = (value: BigNumber.Value): string => {
  const amount = BigNumber(value)

  if (!amount.isFinite()) {
    return '+$0.00'
  }

  const rounded = amount.decimalPlaces(2, BigNumber.ROUND_HALF_UP)
  const sign = rounded.isNegative() ? '-' : '+'

  return `${sign}$${rounded.absoluteValue().toFormat(2)}`
}

/** A ratio as a signed percentage: 0.195 is "+19.5%". */
export const formatPercent = (ratio: BigNumber.Value): string => {
  const value = BigNumber(ratio)

  if (!value.isFinite()) {
    return '+0%'
  }

  const percent = value.multipliedBy(100).decimalPlaces(1, BigNumber.ROUND_DOWN)

  return `${percent.isNegative() ? '' : '+'}${percent.toFormat()}%`
}

/**
 * A token quantity, at the scale the API sends it on.
 *
 * Positions come back at a fixed eighteen places whatever the token's own
 * decimals are, so the sum of a position is the sum of comparable numbers.
 */
export const POSITION_SCALE = 18

/** A held quantity, short enough for a row: "12.4M", "1,204", "0.0031". */
export const formatQuantity = (
  raw: string,
  decimals: number = POSITION_SCALE,
): string => {
  const amount = toDollars(raw, decimals)

  if (amount.isZero()) {
    return '0'
  }

  for (const [size, suffix] of [
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
  ] as const) {
    if (amount.isGreaterThanOrEqualTo(size)) {
      return `${amount.dividedBy(size).toFormat(2)}${suffix}`
    }
  }

  // Below a thousand, places matter more than separators: a holding of
  // 0.0031 of something is a real holding and must not print as 0.
  return amount.isLessThan(1)
    ? amount.precision(3, BigNumber.ROUND_DOWN).toFixed()
    : amount.toFormat(2)
}
